<template>
  <div class="access-tokens-manager mt-4">
    <h2 class="d-inline-block">{{ $t('accessTokens') }}</h2>

    <div v-if="!showCreateForm" class="settings-button d-inline-block ml-2" @click="showCreateForm = true">
      <icon class="mr-1" type="plus"/>
      {{ $t('createNewAccessToken') }}
    </div>

    <div v-if="showCreateForm" class="bg-dark border-radius p-2 mt-2 mb-3">
      <div class="inline-edit-label">
        <b>{{ $t('accessTokenName') }}</b>
      </div>
      <input
          v-model="newTokenName"
          :placeholder="$t('accessTokenNamePlaceholder')"
          class="form-text mb-2"
          type="text"
      >
      <div class="inline-edit-label">
        <b>{{ $t('accessTokenExpiresInDays') }}</b>
      </div>
      <input
          v-model.number="newTokenExpiresInDays"
          class="form-text mb-2"
          max="365"
          min="1"
          type="number"
      >
      <div v-if="error" class="form-error p-2">{{ error }}</div>
      <div class="d-flex">
        <div class="settings-button d-inline-block mr-2" @click="cancelCreate">
          {{ $t('cancel') }}
        </div>
        <div :class="['settings-button d-inline-block', !newTokenName ? 'disabled' : '']" @click="create">
          <icon class="mr-1" type="save"/>
          {{ $t('save') }}
        </div>
      </div>
    </div>

    <div v-if="lastCreatedToken" class="bg-dark border-radius p-2 mt-2 mb-3">
      <h3>{{ $t('accessTokenCreatedTitle') }}</h3>
      <div class="form-error p-2">{{ $t('accessTokenCreatedWarning') }}</div>
      <div class="d-flex align-items-center">
        <code class="p-2 border-radius bg-beige d-inline-block mr-2" style="word-break: break-all;">{{
            lastCreatedToken.token
          }}</code>
        <div class="settings-button d-inline-block" @click="copyToken">
          <icon class="mr-1" type="copy"/>
          {{ copied ? $t('copiedToClipboard') : $t('copyToClipboard') }}
        </div>
      </div>
      <div class="settings-button d-inline-block mt-2" @click="dismissCreated">
        {{ $t('ok') }}
      </div>
    </div>

    <div class="w-100"/>

    <div v-if="!tokens.length" class="lighter mt-2">
      {{ $t('accessTokenNoTokens') }}
    </div>

    <div v-for="pat in tokens" :key="pat.id" class="bg-dark border-radius p-2 mb-2 access-token-row">
      <div class="row">
        <div class="col-12 col-md-4">
          <b>{{ pat.name }}</b>
          <div class="smaller lighter">{{ pat.tokenPrefix }}…</div>
        </div>
        <div class="col-6 col-md-3">
          <div class="smaller lighter">{{ $t('accessTokenExpiresAt') }}</div>
          <div>{{ formatDate(pat.expiresAt) }}</div>
        </div>
        <div class="col-6 col-md-3">
          <div class="smaller lighter">{{ $t('accessTokenLastUsed') }}</div>
          <div>{{ pat.lastUsedAt ? formatDate(pat.lastUsedAt) : $t('accessTokenNeverUsed') }}</div>
        </div>
        <div class="col-12 col-md-2 d-flex align-items-center justify-content-end">
          <div v-if="pat.revokedAt" class="lighter">{{ $t('accessTokenRevoke') }}d</div>
          <div v-else class="settings-button d-inline-block" @click="revoke(pat.id)">
            <icon class="mr-1" type="trash"/>
            {{ $t('accessTokenRevoke') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '../Icon';

export default {
  name: 'AccessTokensManager',
  components: {
    Icon,
  },
  data() {
    return {
      showCreateForm: false,
      newTokenName: '',
      newTokenExpiresInDays: 90,
      error: '',
      copied: false,
    };
  },
  computed: {
    tokens() {
      return this.$store.getters.getAccessTokens;
    },
    lastCreatedToken() {
      return this.$store.getters.getLastCreatedAccessToken;
    },
  },
  created() {
    this.$store.dispatch('loadAccessTokens');
  },
  methods: {
    formatDate(date) {
      return this.$store.getters['convertDate'] ? this.$store.getters.convertDate(date) : new Date(date).toLocaleDateString();
    },
    cancelCreate() {
      this.showCreateForm = false;
      this.newTokenName = '';
      this.newTokenExpiresInDays = 90;
      this.error = '';
    },
    create() {
      if (!this.newTokenName) {
        return;
      }
      this.error = '';
      this.$store.dispatch('createAccessToken', {
        name: this.newTokenName,
        expiresInDays: this.newTokenExpiresInDays || 90,
      }).then(() => {
        this.cancelCreate();
      }).catch(e => {
        this.error = e && e.message ? e.message : String(e);
      });
    },
    revoke(tokenId) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(this.$t('accessTokenRevokeConfirm'))) {
        return;
      }
      this.$store.dispatch('revokeAccessToken', {tokenId});
    },
    copyToken() {
      if (!this.lastCreatedToken) return;
      navigator.clipboard.writeText(this.lastCreatedToken.token).then(() => {
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      });
    },
    dismissCreated() {
      this.$store.dispatch('clearLastCreatedAccessToken');
    },
  },
}
</script>

<style lang="scss">
.access-tokens-manager {
  .access-token-row code {
    font-size: 0.85em;
  }
}
</style>
